/**
 * Roles disponibles en el sistema S-Stock.
 * El valor numérico corresponde al campo `role` en InvUser / stores / branches.
 */
export enum EnumRoles {
  ADMIN      = 0,
  EDITOR     = 1,
  VIEWER     = 2,
  CC_ADMIN   = 3,
  CC_EDITOR  = 4,
  CC_VIEWER  = 5,
  DF_VIEWER  = 6,
}

/** Nombre legible de cada rol. */
export const ROLE_NAMES: Record<EnumRoles, string> = {
  [EnumRoles.ADMIN]:     'Administrador',
  [EnumRoles.EDITOR]:    'Bodeguero',
  [EnumRoles.VIEWER]:    'Auditor',
  [EnumRoles.CC_ADMIN]:  'Encargado de sucursal',
  [EnumRoles.CC_EDITOR]: 'Bodeguero de sucursal',
  [EnumRoles.CC_VIEWER]: 'Auditor de sucursal',
  [EnumRoles.DF_VIEWER]: 'Sin asignar',
};

/** Metadatos para badge visual de cada rol (CSS nativo). */
export interface RoleBadge {
  bg:     string;  // background-color
  text:   string;  // color (texto e ícono)
  border: string;  // border-color
  icon:   string;  // SVG path `d` (viewBox 0 0 24 24)
}

export const ROLE_BADGE: Record<EnumRoles, RoleBadge> = {
  [EnumRoles.ADMIN]:     { bg: 'rgba(220,38,38,0.10)',   text: '#dc2626', border: 'rgba(220,38,38,0.20)',   icon: 'M12 2l9 4.5V12c0 5.52-3.85 10.7-9 12-5.15-1.3-9-6.48-9-12V6.5L12 2z' },
  [EnumRoles.EDITOR]:    { bg: 'rgba(26,127,212,0.10)',  text: '#1a7fd4', border: 'rgba(26,127,212,0.20)',  icon: 'M22 9h-4V7c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v2H2v12h20V9zm-6-2v2H8V7h8z' },
  [EnumRoles.VIEWER]:    { bg: 'rgba(100,116,139,0.10)', text: '#475569', border: 'rgba(100,116,139,0.20)', icon: 'M12 5C7 5 2.73 8.11 1 12.5 2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z' },
  [EnumRoles.CC_ADMIN]:  { bg: 'rgba(217,119,6,0.10)',   text: '#d97706', border: 'rgba(217,119,6,0.20)',   icon: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z' },
  [EnumRoles.CC_EDITOR]: { bg: 'rgba(124,77,255,0.10)',  text: '#7c4dff', border: 'rgba(124,77,255,0.20)',  icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4a2 2 0 001-1.73z' },
  [EnumRoles.CC_VIEWER]: { bg: 'rgba(100,116,139,0.10)', text: '#64748b', border: 'rgba(100,116,139,0.20)', icon: 'M15.5 14h-.79l-.28-.27A6.5 6.5 0 0016 9.5a6.5 6.5 0 10-6.5 6.5 6.5 6.5 0 004.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0a4.5 4.5 0 110-9 4.5 4.5 0 010 9z' },
  [EnumRoles.DF_VIEWER]: { bg: 'rgba(148,163,184,0.10)', text: '#94a3b8', border: 'rgba(148,163,184,0.20)', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z' },
};

/** Metadatos visuales (color e ícono Ionic) de cada rol. */
export const ROLE_META: Record<EnumRoles, { color: string; icon: string }> = {
  [EnumRoles.ADMIN]:     { color: 'danger',    icon: 'shield-outline' },
  [EnumRoles.EDITOR]:    { color: 'primary',   icon: 'briefcase-outline' },
  [EnumRoles.VIEWER]:    { color: 'secondary', icon: 'eye-outline' },
  [EnumRoles.CC_ADMIN]:  { color: 'warning',   icon: 'business-outline' },
  [EnumRoles.CC_EDITOR]: { color: 'tertiary',  icon: 'briefcase-outline' },
  [EnumRoles.CC_VIEWER]: { color: 'medium',    icon: 'search-outline' },
  [EnumRoles.DF_VIEWER]: { color: 'medium',    icon: 'remove-circle-outline' },
};

/**
 * Jerarquía de roles (menor número = más privilegios).
 *
 * | Enum       | id | Scope        |
 * |------------|----|--------------|
 * | ADMIN      |  0 | Global       |
 * | EDITOR     |  1 | Global       |
 * | VIEWER     |  2 | Global       |
 * | CC_ADMIN   |  3 | Branch-level |
 * | CC_EDITOR  |  4 | Branch-level |
 * | CC_VIEWER  |  5 | Branch-level |
 * | DF_VIEWER  |  6 | Global (default sin asignación) |
 */
export const ROLE_SCOPE: Record<EnumRoles, 'global' | 'branch'> = {
  [EnumRoles.ADMIN]:     'global',
  [EnumRoles.EDITOR]:    'global',
  [EnumRoles.VIEWER]:    'global',
  [EnumRoles.CC_ADMIN]:  'branch',
  [EnumRoles.CC_EDITOR]: 'branch',
  [EnumRoles.CC_VIEWER]: 'branch',
  [EnumRoles.DF_VIEWER]: 'global',
};

/** Convierte un número en su EnumRoles correspondiente. Devuelve DF_VIEWER si el valor es inválido. */
export function toEnumRole(value: number): EnumRoles {
  return value in EnumRoles ? (value as EnumRoles) : EnumRoles.DF_VIEWER;
}

/**
 * Resuelve el rol efectivo del usuario aplicando la lógica de priorización de S-Stock.
 *
 * Prioridad (de mayor a menor):
 *  1. ADMIN      → invUser.role === 0
 *  2. EDITOR     → invUser.role === 1  (debe tener al menos una tienda en stores)
 *  3. VIEWER     → invUser.role === 2
 *  4. CC_ADMIN   → invUser.role === DF_VIEWER + branches con role CC_ADMIN
 *  5. CC_EDITOR  → invUser.role === DF_VIEWER + branches con role CC_EDITOR
 *  6. CC_VIEWER  → invUser.role === DF_VIEWER + branches con role CC_VIEWER
 *  7. DF_VIEWER  → fallback (sin asignación)
 */
export function resolveEffectiveRole(
  globalRole: number,
  branches: Array<{ role: number }> = [],
): EnumRoles {
  switch (globalRole) {
    case EnumRoles.ADMIN:  return EnumRoles.ADMIN;
    case EnumRoles.EDITOR: return EnumRoles.EDITOR;
    case EnumRoles.VIEWER: return EnumRoles.VIEWER;
  }

  // Roles de sucursal: globalRole es DF_VIEWER pero tiene branches con rol CC_*
  if (branches.some((b) => b.role === EnumRoles.CC_ADMIN))  return EnumRoles.CC_ADMIN;
  if (branches.some((b) => b.role === EnumRoles.CC_EDITOR)) return EnumRoles.CC_EDITOR;
  if (branches.some((b) => b.role === EnumRoles.CC_VIEWER)) return EnumRoles.CC_VIEWER;

  return EnumRoles.DF_VIEWER;
}
